// IA Modal — a drawer that centers as a dialog and supports STACKING.
//
// Extends IaDrawer so it inherits the trigger wiring ([data-drawer-trigger]),
// overlay-click close, and <ia-drawer-close> support. On top of that it adds a
// shared open-stack so a modal opened from inside another modal layers above it:
//   • each open modal gets a --ia-modal-level (z-index step, see ia-modal.css)
//   • Escape / overlay-click close ONLY the topmost modal
//   • the body scroll-lock is acquired once (first modal) and released once (last)
//   • a lower modal can still be closed directly (its close button / X) — the
//     stack just splices it out and re-levels the rest.
import { IaDrawer } from 'ia-drawer';
import { EVENTS } from 'ia-util-pubsub';
import { lockPage } from 'ia-util-helpers';

// One document-level Escape handler shared by all modals. Registered in the
// CAPTURE phase BEFORE any <ia-modal-component> upgrades, so it runs before the
// per-instance keyup handlers IaDrawer adds in its constructor; stopping
// immediate propagation there prevents the base handlers from closing every
// open modal at once — only the topmost is closed.
function bindStackEscape() {
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape') return;
      if (IaModal.stack.length === 0) return;
      event.stopImmediatePropagation();
      IaModal.stack[IaModal.stack.length - 1].close();
    },
    true
  );
}

export class IaModal extends IaDrawer {
  static stack = [];

  // Move to <body> like a drawer, but first drop any stale same-id instance a
  // previous block re-render (data-re-render → outerHTML swap) left behind, so
  // modals never duplicate on variant change.
  connectedCallback() {
    const id = this.dataset.drawerId;
    if (id) {
      document
        .querySelectorAll(`ia-modal-component[data-drawer-id="${id}"]`)
        .forEach((modal) => {
          if (modal !== this) {
            IaModal.detach(modal);
            modal.remove();
          }
        });
    }
    super.connectedCallback();
  }

  open() {
    if (IaModal.stack.includes(this)) return;
    const level = IaModal.stack.length;
    IaModal.stack.push(this);
    this.style.setProperty('--ia-modal-level', level);
    this.classList.add('is-open');
    this.isOpen = true;
    if (level === 0) IaModal.acquireLock();
    this.dispatchEvent(
      new CustomEvent(EVENTS.drawerOpened, { bubbles: true, detail: { drawerId: this.id } })
    );
  }

  close() {
    if (!IaModal.stack.includes(this)) return;
    this.classList.remove('is-open');
    this.isOpen = false;
    this.style.removeProperty('--ia-modal-level');
    IaModal.detach(this);
  }

  // Remove a modal from the stack and re-level the survivors; release the body
  // lock only once the stack is empty.
  static detach(modal) {
    const index = IaModal.stack.indexOf(modal);
    if (index === -1) return;
    IaModal.stack.splice(index, 1);
    IaModal.stack.forEach((open, level) =>
      open.style.setProperty('--ia-modal-level', level)
    );
    if (IaModal.stack.length === 0) IaModal.releaseLock();
  }

  static acquireLock() {
    if (
      document.documentElement.scrollHeight >
      document.documentElement.clientHeight
    ) {
      lockPage(true);
    }
    window.iaTheme?.hideExtraStickyElements?.();
  }

  static releaseLock() {
    lockPage(false);
    window.iaTheme?.showExtraStickyElements?.();
  }
}

bindStackEscape();

if (!customElements.get('ia-modal-component')) {
  customElements.define('ia-modal-component', IaModal);
}
